import { collectManagedImages, syncSourceHostedImages } from '../../../utils/canonicalProductImages';
import type { AdminCanonicalProduct, AdminProductImage, AdminPromotion, Timestampish } from './productDetailTypes';

/**
 * The editable copy of a canonical product the panel holds while editing.
 * Everything is a string or a plain list so inputs stay controlled; the patch
 * builder turns it back into the document shape and sends only what changed,
 * because `PUT /normalization/canonical/:id` replaces any nested object it is
 * sent (pricing excepted, which it deep-merges).
 */

export type YesNo = 'Yes' | 'No';

export interface SpecRow {
  key: string;
  value: string;
}

export interface SpecGroupDraft {
  /** '' is the flat (ungrouped) bucket at the top level of `specifications`. */
  name: string;
  rows: SpecRow[];
  /** A real section (object) rather than the flat bucket, even while its name is being retyped. */
  isGroup?: boolean;
}

export interface DimsDraft {
  length: string;
  width: string;
  height: string;
  weight: string;
}

export interface ImageDraft {
  url: string;
  alt: string;
  isPrimary: boolean;
  type: string;
  typeSource: string;
  imageSource: string;
}

export interface PromotionDraft {
  id: string;
  name: string;
  amount: string;
  startsAt: string;
  endsAt: string;
  stackable: YesNo;
  type: string;
  source: string;
}

export interface ProductDraft {
  name: string;
  shortDescription: string;
  description: string;
  brand: string;
  model: string;
  sku: string;
  mpn: string;
  upc: string;
  ean: string;
  gtin: string;
  pricingAliases: string[];
  tags: string[];
  categoryPath: string;
  category: string;
  categorySlug: string;
  productType: string;
  productTypeExpanded: string;
  status: string;
  inStock: YesNo;
  discontinued: YesNo;
  launchAnnounced: string;
  launchUs: string;
  launchCanada: string;
  launchDate: string;
  features: string[];
  inTheBox: Array<{ quantity: string; name: string; sku?: string }>;
  isProOnly: YesNo;
  proSiteUrl: string;
  images: ImageDraft[];
  videos: Array<{ title: string; url: string; type: string }>;
  documentation: Array<{ title: string; url: string; type: string; language: string }>;
  specGroups: SpecGroupDraft[];
  dimensions: DimsDraft;
  shippingDimensions: DimsDraft;
  msrp: string;
  map: string;
  retail: string;
  salePrice: string;
  instantRebate: string;
  wholesale: string;
  tradeCredit: string;
  salePriceSource: string;
  currency: string;
  rebateStartAt: string;
  rebateEndAt: string;
  effectiveDate: string;
  expirationDate: string;
  manufacturerPriceAsOf: string;
  manufacturerPriceEndAt: string;
  mapStartAt: string;
  mapEndAt: string;
  salePriceStartAt: string;
  salePriceEndAt: string;
  promotions: PromotionDraft[];
  specificationsRawJson: string;
  /** Break-glass JSON of every field the tabs do not edit (see `documentJsonFrom`). */
  documentJson: string;
}

/** A Firestore timestamp in any of the shapes the API serializes, as a Date. */
export function toDate(value: Timestampish | Date): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object') {
    const seconds = '_seconds' in value ? value._seconds : 'seconds' in value ? value.seconds : undefined;
    return typeof seconds === 'number' ? new Date(seconds * 1000) : null;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `YYYY-MM-DD` for a date input, or ''. */
export function dateInput(value: Timestampish | Date): string {
  const date = toDate(value);
  return date ? date.toISOString().slice(0, 10) : '';
}

const num = (value: number | undefined | null): string =>
  value == null || !Number.isFinite(value) ? '' : String(value);

const yn = (value: boolean | undefined): YesNo => (value ? 'Yes' : 'No');

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

/** Spec values are strings, numbers, booleans or short arrays; arrays read as a comma list. */
export function specValueText(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) return value.map(specValueText).filter(Boolean).join(', ');
  if (isPlainObject(value)) return JSON.stringify(value);
  return String(value);
}

/**
 * Specs as groups: each top-level object becomes a group (its nested objects
 * flatten into dotted keys); top-level scalars land in the '' bucket.
 */
export function specGroupsFrom(specifications: Record<string, unknown> | undefined): SpecGroupDraft[] {
  const flat: SpecRow[] = [];
  const groups: SpecGroupDraft[] = [];
  const flatten = (obj: Record<string, unknown>, prefix: string, out: SpecRow[]) => {
    for (const [key, value] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (isPlainObject(value)) flatten(value, path, out);
      else out.push({ key: path, value: specValueText(value) });
    }
  };
  for (const [key, value] of Object.entries(specifications || {})) {
    if (isPlainObject(value)) {
      const rows: SpecRow[] = [];
      flatten(value, '', rows);
      groups.push({ name: key, rows, isGroup: true });
    } else {
      flat.push({ key, value: specValueText(value) });
    }
  }
  return flat.length ? [{ name: '', rows: flat }, ...groups] : groups;
}

/** Inverse of `specGroupsFrom`; keeps the original value when its text did not change. */
export function specificationsFrom(groups: SpecGroupDraft[], original: Record<string, unknown> | undefined): Record<string, unknown> {
  const readPath = (root: unknown, path: string[]): unknown =>
    path.reduce<unknown>((node, key) => (isPlainObject(node) ? node[key] : undefined), root);
  const result: Record<string, unknown> = {};
  for (const group of groups) {
    // A section whose name was cleared files its rows at the top level, like the flat bucket.
    const name = group.name.trim();
    const base = name ? [name] : [];
    for (const row of group.rows) {
      const key = row.key.trim();
      if (!key) continue;
      const path = [...base, ...key.split('.').map((s) => s.trim()).filter(Boolean)];
      const prior = readPath(original, path);
      const value = prior !== undefined && specValueText(prior) === row.value ? prior : row.value;
      let node = result;
      path.forEach((segment, i) => {
        if (i === path.length - 1) node[segment] = value;
        else {
          if (!isPlainObject(node[segment])) node[segment] = {};
          node = node[segment] as Record<string, unknown>;
        }
      });
    }
  }
  return result;
}

const dims = (d: AdminCanonicalProduct['dimensions']): DimsDraft => ({
  length: num(d?.length),
  width: num(d?.width),
  height: num(d?.height),
  weight: num(d?.weight),
});

export function imagesFrom(product: AdminCanonicalProduct): ImageDraft[] {
  const meta = new Map<string, AdminProductImage>();
  for (const image of product.images || []) {
    if (image && typeof image === 'object' && image.url) meta.set(image.url.toLowerCase(), image);
  }
  return collectManagedImages(product as Parameters<typeof collectManagedImages>[0]).map((row) => {
    const m = meta.get(row.url.toLowerCase());
    return {
      url: row.url,
      alt: row.alt,
      isPrimary: row.isPrimary,
      type: m?.type || '',
      typeSource: m?.typeSource || '',
      imageSource: m?.imageSource || '',
    };
  });
}

export function draftFromProduct(product: AdminCanonicalProduct): ProductDraft {
  const p = product.pricing || {};
  const ld = product.launchDates || {};
  return {
    name: product.name || '',
    shortDescription: product.shortDescription || '',
    description: product.description || '',
    brand: product.brand || '',
    model: product.model || '',
    sku: product.sku || '',
    mpn: product.mpn || '',
    upc: product.upc || '',
    ean: product.ean || '',
    gtin: product.gtin || '',
    pricingAliases: [...(product.pricingAliases || [])],
    tags: [...(product.tags || [])],
    categoryPath: (product.categoryPath || []).join(' / '),
    category: product.category || '',
    categorySlug: product.categorySlug || '',
    productType: product.productType || '',
    productTypeExpanded: product.productTypeExpanded || '',
    status: product.status || 'active',
    inStock: yn(product.inventory?.inStock),
    discontinued: yn(product.inventory?.discontinued),
    launchAnnounced: ld.announced || '',
    launchUs: ld.us?.date || '',
    launchCanada: ld.canada?.date || '',
    launchDate: product.launchDate || '',
    features: [...(product.features || [])],
    inTheBox: (product.inTheBox || []).map((item) => ({ quantity: String(item.quantity ?? 1), name: item.name || '', ...(item.sku ? { sku: item.sku } : {}) })),
    isProOnly: yn(product.isProOnly),
    proSiteUrl: product.proSiteUrl || '',
    images: imagesFrom(product),
    videos: (product.videos || []).map((v) => ({ title: v.title || '', url: v.url || '', type: v.type || '' })),
    documentation: (product.documentation || []).map((d) => ({ title: d.title || '', url: d.url || '', type: d.type || '', language: d.language || '' })),
    specGroups: specGroupsFrom(product.specifications),
    dimensions: dims(product.dimensions),
    shippingDimensions: dims(product.shippingDimensions),
    msrp: num(p.msrp),
    map: num(p.map),
    retail: num(p.retail),
    salePrice: num(p.salePrice),
    instantRebate: num(p.instantRebate),
    wholesale: num(p.wholesale),
    tradeCredit: num(p.tradeCredit),
    salePriceSource: p.salePriceSource || '',
    currency: p.currency || 'USD',
    rebateStartAt: dateInput(p.rebateStartAt),
    rebateEndAt: dateInput(p.rebateEndAt),
    effectiveDate: dateInput(p.effectiveDate),
    expirationDate: dateInput(p.expirationDate),
    manufacturerPriceAsOf: dateInput(p.manufacturerPriceAsOf),
    manufacturerPriceEndAt: dateInput(p.manufacturerPriceEndAt),
    mapStartAt: dateInput(p.mapStartAt),
    mapEndAt: dateInput(p.mapEndAt),
    salePriceStartAt: dateInput(p.salePriceStartAt),
    salePriceEndAt: dateInput(p.salePriceEndAt),
    promotions: (p.activePromotions || []).map((promo: AdminPromotion) => ({
      id: promo.id,
      name: promo.name || '',
      amount: num(promo.amount),
      startsAt: dateInput(promo.startsAt),
      endsAt: dateInput(promo.endsAt),
      stackable: yn(promo.stackable),
      type: promo.type || 'instant_rebate',
      source: promo.source || 'manual',
    })),
    specificationsRawJson: JSON.stringify(product.specificationsRaw || {}, null, 2),
    documentJson: JSON.stringify(documentJsonFrom(product), null, 2),
  };
}

/**
 * Keys the document JSON editor never offers: identity and timestamps, the
 * lock and provenance records the server owns, and specs (edited on Specs and
 * as specificationsRaw).
 */
export const DOCUMENT_JSON_EXCLUDED_KEYS = ['id', 'createdAt', 'updatedAt', 'promotedAt', 'manualFields', 'provenance', 'specifications', 'specificationsRaw'];

/** The document as the full-document JSON editor shows it. */
export function documentJsonFrom(product: AdminCanonicalProduct): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(product)) {
    if (!DOCUMENT_JSON_EXCLUDED_KEYS.includes(key)) out[key] = value;
  }
  return out;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

const priceOrNull = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const dimsPatch = (draft: DimsDraft, original: AdminCanonicalProduct['dimensions']) => {
  const out: Record<string, unknown> = { ...(original || {}) };
  for (const key of ['length', 'width', 'height', 'weight'] as const) {
    const value = priceOrNull(draft[key]);
    if (value == null) delete out[key];
    else out[key] = value;
  }
  if (!out.unit) out.unit = 'mm';
  if (out.weight != null && !out.weightUnit) out.weightUnit = 'g';
  return out;
};

export class DraftValidationError extends Error {}

/**
 * The PUT body for what changed between `base` and `draft`, and the list of
 * changed field paths (used for the "You're editing this" notes and the
 * manual-field lock). Pricing keys are sent individually under `pricing`.
 */
export function buildPatch(
  product: AdminCanonicalProduct,
  base: ProductDraft,
  draft: ProductDraft,
): { payload: Record<string, unknown>; changed: string[] } {
  const payload: Record<string, unknown> = {};
  const pricing: Record<string, unknown> = {};
  const changed: string[] = [];
  const set = (path: string, value: unknown) => {
    payload[path] = value;
    changed.push(path);
  };

  const text = ['name', 'shortDescription', 'description', 'brand', 'model', 'sku', 'mpn', 'upc', 'ean', 'gtin', 'category', 'categorySlug', 'productType', 'productTypeExpanded', 'status', 'proSiteUrl'] as const;
  for (const key of text) {
    if (draft[key] !== base[key]) set(key, draft[key].trim());
  }
  if (!same(draft.pricingAliases, base.pricingAliases)) set('pricingAliases', draft.pricingAliases);
  if (!same(draft.tags, base.tags)) set('tags', draft.tags);
  if (draft.categoryPath !== base.categoryPath) {
    set('categoryPath', draft.categoryPath.split(/\s*[/›]\s*/).map((s) => s.trim()).filter(Boolean));
  }
  if (draft.isProOnly !== base.isProOnly) set('isProOnly', draft.isProOnly === 'Yes');
  if (draft.launchDate !== base.launchDate) set('launchDate', draft.launchDate.trim() || null);

  if (draft.inStock !== base.inStock || draft.discontinued !== base.discontinued) {
    const inventory: Record<string, unknown> = { ...(product.inventory || {}) };
    if (draft.inStock !== base.inStock) inventory.inStock = draft.inStock === 'Yes';
    if (draft.discontinued !== base.discontinued) inventory.discontinued = draft.discontinued === 'Yes';
    set('inventory', inventory);
  }

  if (draft.launchAnnounced !== base.launchAnnounced || draft.launchUs !== base.launchUs || draft.launchCanada !== base.launchCanada) {
    const ld: Record<string, unknown> = { ...(product.launchDates || {}), source: 'manual' };
    const region = (value: string, prior?: { precision?: string; note?: string }) =>
      value.trim() ? { ...(prior || {}), date: value.trim(), precision: prior?.precision || (/^\d{4}-\d{2}$/.test(value.trim()) ? 'month' : 'exact') } : undefined;
    ld.announced = draft.launchAnnounced.trim() || undefined;
    ld.us = region(draft.launchUs, product.launchDates?.us);
    ld.canada = region(draft.launchCanada, product.launchDates?.canada);
    for (const key of Object.keys(ld)) if (ld[key] === undefined) delete ld[key];
    set('launchDates', ld);
  }

  if (!same(draft.features, base.features)) set('features', draft.features.map((f) => f.trim()).filter(Boolean));
  if (!same(draft.inTheBox, base.inTheBox)) {
    set(
      'inTheBox',
      draft.inTheBox
        .filter((item) => item.name.trim())
        .map((item) => ({ name: item.name.trim(), quantity: Math.max(1, Number(item.quantity) || 1), ...(item.sku ? { sku: item.sku } : {}) })),
    );
  }

  if (!same(draft.images, base.images)) {
    const stored = new Map<string, AdminProductImage>();
    for (const image of product.images || []) {
      if (image && typeof image === 'object' && image.url) stored.set(image.url.toLowerCase(), image);
    }
    const images = draft.images
      .filter((img) => img.url.trim())
      .map((img) => {
        const url = img.url.trim();
        // Keep fields the draft does not edit (sourceUrl and the like).
        const out: Record<string, unknown> = { ...(stored.get(url.toLowerCase()) || {}), url };
        for (const key of ['alt', 'isPrimary', 'type', 'typeSource', 'imageSource']) delete out[key];
        if (img.alt.trim()) out.alt = img.alt.trim();
        if (img.isPrimary) out.isPrimary = true;
        if (img.type) out.type = img.type;
        if (img.typeSource) out.typeSource = img.typeSource;
        if (img.imageSource) out.imageSource = img.imageSource;
        return out;
      });
    if (images.length && !images.some((img) => img.isPrimary)) images[0].isPrimary = true;
    set('images', images);
    payload.source = syncSourceHostedImages(product.source as Record<string, any> | undefined, images.map((img) => String(img.url)));
  }
  if (!same(draft.videos, base.videos)) {
    set('videos', draft.videos.filter((v) => v.url.trim()).map((v) => ({ url: v.url.trim(), title: v.title.trim() || undefined, type: v.type || undefined })));
  }
  if (!same(draft.documentation, base.documentation)) {
    set(
      'documentation',
      draft.documentation
        .filter((d) => d.url.trim())
        .map((d) => ({ url: d.url.trim(), title: d.title.trim() || d.url.trim(), type: d.type || 'other', ...(d.language ? { language: d.language } : {}) })),
    );
  }

  if (!same(draft.specGroups, base.specGroups)) set('specifications', specificationsFrom(draft.specGroups, product.specifications));
  if (!same(draft.dimensions, base.dimensions)) set('dimensions', dimsPatch(draft.dimensions, product.dimensions));
  if (!same(draft.shippingDimensions, base.shippingDimensions)) set('shippingDimensions', dimsPatch(draft.shippingDimensions, product.shippingDimensions));

  if (draft.specificationsRawJson !== base.specificationsRawJson) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(draft.specificationsRawJson.trim() || '{}');
    } catch {
      throw new DraftValidationError('specificationsRaw is not valid JSON.');
    }
    if (!isPlainObject(parsed)) throw new DraftValidationError('specificationsRaw must be a JSON object.');
    set('specificationsRaw', parsed);
  }

  const priceKeys = ['msrp', 'map', 'retail', 'salePrice', 'instantRebate', 'wholesale', 'tradeCredit'] as const;
  for (const key of priceKeys) {
    if (draft[key] !== base[key]) {
      pricing[key] = priceOrNull(draft[key]);
      changed.push(`pricing.${key}`);
    }
  }
  const dateKeys = ['manufacturerPriceAsOf', 'manufacturerPriceEndAt', 'mapStartAt', 'mapEndAt', 'salePriceStartAt', 'salePriceEndAt', 'rebateStartAt', 'rebateEndAt', 'effectiveDate', 'expirationDate'] as const;
  for (const key of dateKeys) {
    if (draft[key] !== base[key]) {
      pricing[key] = draft[key] || null;
      changed.push(`pricing.${key}`);
    }
  }
  if (draft.currency !== base.currency) {
    pricing.currency = draft.currency || 'USD';
    changed.push('pricing.currency');
  }
  const saleTouched = ['salePrice', 'salePriceStartAt', 'salePriceEndAt'].some((k) => changed.includes(`pricing.${k}`));
  if (draft.salePriceSource !== base.salePriceSource) {
    pricing.salePriceSource = draft.salePriceSource || null;
    changed.push('pricing.salePriceSource');
  } else if (saleTouched) {
    // An admin-set sale is authoritative, so scrapes never overwrite it (utils/saleOwnership).
    pricing.salePriceSource = priceOrNull(draft.salePrice) != null ? 'manual' : null;
  }
  if (!same(draft.promotions, base.promotions)) {
    pricing.activePromotions = draft.promotions
      .filter((promo) => promo.name.trim())
      .map((promo) => ({
        id: promo.id,
        type: promo.type || 'instant_rebate',
        name: promo.name.trim(),
        amount: priceOrNull(promo.amount) ?? 0,
        ...(promo.startsAt ? { startsAt: promo.startsAt } : {}),
        ...(promo.endsAt ? { endsAt: promo.endsAt } : {}),
        source: promo.source || 'manual',
        stackable: promo.stackable === 'Yes',
        active: true,
      }));
    changed.push('pricing.activePromotions');
  }
  if (draft.documentJson !== base.documentJson) {
    applyDocumentJson(product, draft.documentJson, payload, pricing, changed);
  }
  if (Object.keys(pricing).length) payload.pricing = pricing;

  return { payload, changed };
}

/**
 * Top-level fields changed in the full-document JSON, sent on their own.
 * A field also edited in a tab keeps the tab's value (the tab is more specific);
 * a key removed from the JSON is not deleted, matching the PUT's merge.
 */
function applyDocumentJson(
  product: AdminCanonicalProduct,
  json: string,
  payload: Record<string, unknown>,
  pricing: Record<string, unknown>,
  changed: string[],
): void {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json.trim() || '{}');
  } catch {
    throw new DraftValidationError('The full document is not valid JSON.');
  }
  if (!isPlainObject(parsed)) throw new DraftValidationError('The full document must be a JSON object.');
  const original = documentJsonFrom(product);
  for (const [key, value] of Object.entries(parsed)) {
    if (DOCUMENT_JSON_EXCLUDED_KEYS.includes(key) || same(value, original[key])) continue;
    if (key === 'pricing') {
      if (!isPlainObject(value)) throw new DraftValidationError('pricing must be a JSON object.');
      const before = isPlainObject(original.pricing) ? original.pricing : {};
      for (const [pk, pv] of Object.entries(value)) {
        if (pk in pricing || same(pv, before[pk])) continue;
        pricing[pk] = pv;
        changed.push(`pricing.${pk}`);
      }
      continue;
    }
    if (key in payload) continue;
    payload[key] = value;
    changed.push(key);
  }
}

/** Field paths a staged diff row touches, for "You're editing this". */
export function draftTouches(changed: string[], path: string): boolean {
  return changed.some((c) => path === c || path.startsWith(`${c}.`) || c.startsWith(`${path}.`));
}

/**
 * Fields a store or vendor may change on its own submitted product (MOT-695),
 * mirroring `submissionProductEditSchema` on the API, which refuses any other
 * key. Nested rows keep only the keys that schema reads, so a stored image or
 * dimension carrying extra fields (a hosted copy's `hostedAt`, a box `source`)
 * still saves.
 */
export const SUBMITTER_EDITABLE_FIELDS = [
  'name', 'shortDescription', 'description', 'model', 'mpn', 'upc', 'ean', 'gtin',
  'category', 'categorySlug', 'categoryPath', 'productType', 'productTypeExpanded',
  'tags', 'features', 'inTheBox', 'images', 'videos', 'documentation', 'specifications',
  'dimensions', 'shippingDimensions', 'launchDate', 'launchDates',
] as const;

const IMAGE_KEYS = ['url', 'sourceUrl', 'alt', 'isPrimary', 'sortOrder', 'width', 'height', 'type', 'typeSource', 'imageSource'];
const VIDEO_KEYS = ['url', 'title', 'type'];
const DIMENSION_KEYS = ['length', 'width', 'height', 'diameter', 'weight', 'unit', 'weightUnit'];

const pickKeys = (value: unknown, keys: string[]): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  if (!isPlainObject(value)) return out;
  for (const key of keys) if (value[key] !== undefined && value[key] !== null) out[key] = value[key];
  return out;
};

/** The editor's patch cut to what a submitter may save; `dropped` names any field that was left out. */
export function submitterPatch(payload: Record<string, unknown>): { payload: Record<string, unknown>; dropped: string[] } {
  const out: Record<string, unknown> = {};
  const dropped: string[] = [];
  for (const [key, value] of Object.entries(payload)) {
    if (!(SUBMITTER_EDITABLE_FIELDS as readonly string[]).includes(key)) {
      // `source` rides along with an image edit for the hosted-copy bookkeeping only Motzu keeps.
      if (key !== 'source') dropped.push(key);
      continue;
    }
    if (key === 'images' && Array.isArray(value)) out.images = value.map((image) => pickKeys(image, IMAGE_KEYS));
    else if (key === 'videos' && Array.isArray(value)) out.videos = value.map((video) => pickKeys(video, VIDEO_KEYS));
    else if ((key === 'dimensions' || key === 'shippingDimensions') && isPlainObject(value)) out[key] = pickKeys(value, DIMENSION_KEYS);
    else out[key] = value;
  }
  return { payload: out, dropped };
}

/**
 * A blank product for the editor's create mode (MOT-701): what a store or
 * vendor fills in when it submits a product Motzu does not carry.
 */
export const BLANK_SUBMISSION_PRODUCT: AdminCanonicalProduct = { id: '', name: '', brand: '', model: '', sku: '' };

/** The one-product submission body (`POST /{store|vendor}/product-submissions`). */
export interface DraftSubmissionRow {
  brand: string;
  model: string;
  name?: string;
  upc?: string;
  sku?: string;
  msrp?: string;
  map?: string;
  category?: string;
  description?: string;
  imageUrls?: string[];
}

export interface DraftSubmission {
  /** What the submission matches and is created on: identity, the submitter's SKU and prices, category. */
  row: DraftSubmissionRow;
  /** Everything else the editor filled in, in `submissionProductEditSchema`'s shape; null when nothing was. */
  details: Record<string, unknown> | null;
  /**
   * The same product cut to the plain upload row ("Keep this private to my
   * store", which takes no details): description and image URLs ride on the
   * row, everything else is left out and named in `privateDropped`.
   */
  privateRow: DraftSubmissionRow;
  privateDropped: string[];
}

/** Row fields; never repeated in `details`, so the two cannot disagree. */
const ROW_FIELDS = ['name', 'model', 'upc', 'category'];
/** The store upload's image cap (`MAX_UPLOAD_IMAGES` on the API). */
const PRIVATE_IMAGE_LIMIT = 10;

/**
 * The editor's draft as a submission (MOT-701). Identity, the submitter's SKU,
 * MSRP, MAP and category form the row the submission is matched and created
 * on; the rest is the `details` block the API saves through the submitter's
 * own edit path, the same one the editor uses after the fact.
 */
export function submissionFromDraft(draft: ProductDraft): DraftSubmission {
  const base = draftFromProduct(BLANK_SUBMISSION_PRODUCT);
  const { payload } = buildPatch(BLANK_SUBMISSION_PRODUCT, base, draft);
  const allowed = submitterPatch(payload).payload;
  const details: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(allowed)) {
    if (!ROW_FIELDS.includes(key)) details[key] = value;
  }
  const opt = (value: string) => value.trim() || undefined;
  const row: DraftSubmissionRow = {
    brand: draft.brand.trim(),
    model: draft.model.trim(),
    name: opt(draft.name),
    upc: opt(draft.upc),
    sku: opt(draft.sku),
    msrp: opt(draft.msrp),
    map: opt(draft.map),
    category: opt(draft.category),
  };
  const images = draft.images.map((image) => image.url.trim()).filter(Boolean);
  const privateRow: DraftSubmissionRow = {
    ...row,
    description: opt(draft.description),
    ...(images.length ? { imageUrls: images.slice(0, PRIVATE_IMAGE_LIMIT) } : {}),
  };
  const privateDropped = Object.keys(details).filter((key) => key !== 'description' && key !== 'images');
  if (images.length > PRIVATE_IMAGE_LIMIT) privateDropped.push('images');
  return { row, details: Object.keys(details).length ? details : null, privateRow, privateDropped };
}
